import { ParsedUrlQuery } from 'querystring';

import React from 'react';
import { useRouter } from 'next/router';
import type { GetServerSideProps, NextPage } from 'next';
import Head from 'next/head';

import { Category as CategoryView } from 'components/views/Category';
import { StrapiService } from 'lib/strapi/StrapiService';
import { StrapiArticle, StrapiCategory, StrapiResponse } from 'types/strapiService/types';

interface Props {
  articles: StrapiResponse<StrapiArticle[]> | null;
  category: StrapiCategory;
  mostPopularArticles: StrapiArticle[] | null;
}

const Category: NextPage<Props> = ({ articles, category, mostPopularArticles }) => {
  const router = useRouter();

  // Skapa en URL utan `page`-parametern
  const currentUrl = new URL(router.asPath, `https://${process.env.NEXT_PUBLIC_SITE_DOMAIN}`);

  currentUrl.searchParams.delete('page'); // Tar bort `page` från query-parametrarna
  const canonicalUrl = currentUrl.toString();

  return (
    <>
      <Head>
        <title>{category.attributes.name} - Blipp</title>
        <meta property="og:title" content={`${category.attributes.name} - Blipp`} />
        <meta
          name="description"
          content={`Upptäck artiklar om bilköp, försäljning och leasing på ${category.attributes.name}. Få experttips och guider för ett tryggt bilägande och bättre affärer.`}
        />
        <meta
          property="og:description"
          content={`Upptäck artiklar om bilköp, försäljning och leasing på ${category.attributes.name}. Få experttips och guider för ett tryggt bilägande och bättre affärer.`}
        />
        <link rel="canonical" key="canonical" href={canonicalUrl} />
      </Head>

      <CategoryView
        articles={articles}
        category={category}
        mostPopularArticles={mostPopularArticles}
      />
    </>
  );
};

export default Category;

interface Params extends ParsedUrlQuery {
  category: string;
}

export const getServerSideProps: GetServerSideProps<Props> = async ({ params, query }) => {
  const page = query.page ? parseInt(query.page as string, 10) : 1;
  const { category: slug } = params as Params;

  const [articles, category, mostPopularArticles] = await Promise.all([
    StrapiService.getArticlesByCategorySlug(slug, page),
    StrapiService.getCategoryBySlug(slug),
    StrapiService.getArticlesByMostPopular(),
  ]);

  if (!category || Object.keys(category).length === 0) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      articles,
      category,
      mostPopularArticles,
    },
  };
};
